import { Plus } from "lucide-react";
import { useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type NosProjectSearchResult,
  useAddPeerExclusion,
  usePeerExclusions,
  useRemovePeerExclusion,
  useSearchNosProjects,
} from "@/hooks/use-review-peer-exclusions";

/**
 * The denylist behind peer derivation — EPIC-056 WI-201. Shared time on
 * "Vacation", "1on1 Sessions" or any other internal bucket would otherwise
 * pair up most of the company as each other's reviewers (WI-200); this is
 * how HR keeps that list current without a code change.
 *
 * Searches NOS live rather than Enable's own project list — Enable doesn't
 * sync projects at all, so there's nothing local to search.
 */
export function PeerExclusionsList() {
  const { data, isLoading, isError, error, refetch } = usePeerExclusions();
  const remove = useRemovePeerExclusion();
  const rows = data ?? [];

  return (
    <DetailSection title="Peer review exclusions" headerActions={<AddExclusionControl />}>
      <p className="mb-3 text-muted-foreground text-sm">
        Projects that never count toward pairing people as each other's peer reviewers — internal
        time buckets like 1:1s, vacation and onboarding, not client work.
      </p>
      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Couldn't load the exclusion list"}
          onRetry={() => refetch()}
        />
      ) : isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing excluded yet"
          description="Every NOS project counts toward peer derivation until it's added here."
        />
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => (
            <li key={row.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3">
              <span className="truncate font-medium text-sm">{row.projectName}</span>
              <ConfirmDialog
                title="Stop excluding this project?"
                description="Shared time on it will count toward peer derivation again the next time a round generates."
                confirmLabel="Stop excluding"
                destructive
                onConfirm={() => remove.mutate(row.id)}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    disabled={remove.isPending}
                  >
                    Remove
                  </Button>
                }
              />
            </li>
          ))}
        </ul>
      )}
    </DetailSection>
  );
}

function AddExclusionControl() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: results, isLoading } = useSearchNosProjects(debouncedSearch);
  const add = useAddPeerExclusion();

  const choose = (project: NosProjectSearchResult) => {
    add.mutate(
      { nosProjectId: project.projectId, projectName: project.projectName },
      {
        onSuccess: () => {
          setOpen(false);
          setSearch("");
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="size-4" aria-hidden />
          Exclude a project
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exclude a project</DialogTitle>
          <DialogDescription>
            Search NOS for the project — logged time against it will never count toward pairing
            people as peers.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="mb-1.5 block" htmlFor="peer-exclusion-search">
            Project name
          </Label>
          <Input
            id="peer-exclusion-search"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search.trim().length > 0 && (
            <ul className="mt-2 max-h-64 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {isLoading ? (
                <li className="p-2 text-muted-foreground text-sm">Searching…</li>
              ) : (results ?? []).length === 0 ? (
                <li className="p-2 text-muted-foreground text-sm">No projects match.</li>
              ) : (
                (results ?? []).map((r) => (
                  <li key={r.projectId}>
                    <button
                      type="button"
                      className="w-full p-2 text-left text-sm hover:bg-muted/40"
                      disabled={add.isPending}
                      onClick={() => choose(r)}
                    >
                      {r.projectName}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
